import React, { useEffect } from 'react';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import ErrorState from '@atlassian/jira-common-components-error-state';
import { JSErrorBoundary } from '@atlassian/jira-error-boundaries/src/ui/js-error-boundary/index.tsx';
import { useIntl } from '@atlassian/jira-intl';
import {
	SCREEN,
	ContextualAnalyticsData,
	FireScreenAnalytics,
} from '@atlassian/jira-product-analytics-bridge';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { GlobalPageLoadExperience, useUFOComponentExperience } from '@atlassian/ufo';
import {
	ExternalLinksContainer,
	useExternalLinks,
	useIsDeleting,
} from './controllers/store/index.tsx';
import { externalResourcesReorderExperience } from './experiences';
import messages from './messages';
import { Links } from './ui/links/index.tsx';
import { isNetworkError } from './utils';

export function ExternalLinksView() {
	useUFOComponentExperience(externalResourcesReorderExperience);
	const [
		{ data, isLoading, error },
		{ fetchExternalLinks, deleteExternalLink, reorderExternalLink },
	] = useExternalLinks();
	const [isDeleting] = useIsDeleting();
	const { formatMessage } = useIntl();

	useEffect(() => {
		fetchExternalLinks();
	}, [fetchExternalLinks]);

	if (error) {
		if (!isNetworkError(error)) {
			GlobalPageLoadExperience.failure({
				force: true,
				metadata: {
					error: error.reason,
				},
			});
		}

		return (
			<ErrorState
				header={formatMessage(messages.errorHeader)}
				description={formatMessage(messages.errorDescription)}
			/>
		);
	}

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const markExperienceFailed = (location: any, err: Error) => {
		GlobalPageLoadExperience.failure({
			force: true,
			metadata: {
				error: err.message,
			},
		});
	};

	return (
		<JSErrorBoundary
			id="externalResourcesView"
			packageName="jiraServicedeskExternalResourcesSettings"
			teamName="jsm-nebula"
			fallback="page"
			onError={markExperienceFailed}
		>
			<Links
				links={data}
				isLoading={isLoading}
				isDeleting={isDeleting}
				onDelete={deleteExternalLink}
				onReorder={reorderExternalLink}
			/>
		</JSErrorBoundary>
	);
}

export const ExternalLinks = ({ projectId }: { projectId: string }) => {
	const cloudId = useCloudId();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	return (
		<ExternalLinksContainer
			cloudId={cloudId}
			projectId={projectId}
			createAnalyticsEvent={createAnalyticsEvent}
		>
			<ContextualAnalyticsData sourceName="jsmExternalResourcesSettings" sourceType={SCREEN}>
				<ExternalLinksView />
				<FireScreenAnalytics />
			</ContextualAnalyticsData>
		</ExternalLinksContainer>
	);
};

export default ExternalLinks;
